import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MediaUpload } from '@/components/common/MediaUpload';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { ResponsiveCombobox, type ComboboxItem } from '@/components/ui/responsive-combobox';
import { useVenues } from '@/features/venues/hooks/useVenueManagement';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { createPromoOfferSchema, type CreatePromoOfferInput } from '../../schemas/promoOfferSchemas';

interface CreatePromoOfferFormProps {
  onSubmit: (values: CreatePromoOfferInput) => Promise<void>;
  formId?: string;
  onResetComplete?: () => void;
}

export function CreatePromoOfferForm({ 
  onSubmit, 
  formId = 'create-promo-offer-form', 
  onResetComplete 
}: CreatePromoOfferFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const uploadMutation = useMediaUpload({ folder: 'offers' });
  
  // Fetch venues dynamically
  const { data: venueData, isLoading: isLoadingVenues } = useVenues();
  
  const venues: ComboboxItem[] = (venueData?.venues || []).map(venue => ({
    value: venue.id.toString(),
    label: venue.name,
  }));

  const form = useForm({
    resolver: zodResolver(createPromoOfferSchema),
    defaultValues: {
      venue_id: 0,
      brand: '',
      short_description: '',
      detailed_description: '',
      media_url: '',
      expires_at: undefined,
      max_claim_per_person: 1,
      max_claim_total: 1,
      is_active: true,
    },
  });

  const handleMediaUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      setIsUploading(false);
      return response;
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const handleSubmit = async (values: CreatePromoOfferInput) => {
    try {
      await onSubmit(values);
      form.reset();
      onResetComplete?.();
    } catch {
      // Error is handled by the parent component (e.g., via toast in the mutation)
      // We don't reset the form on error so user can correct and retry
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        className="space-y-6"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-testid="promo-offer-form"
      >
        <FormField
          control={form.control}
          name="venue_id"
          render={({ field }) => {
            const selectedVenue = venueData?.venues.find(v => v.id === field.value);
            return (
              <FormItem>
                <FormLabel>Venue *</FormLabel>
                <FormControl>
                  <ResponsiveCombobox
                    items={venues}
                    value={field.value && field.value > 0 ? field.value.toString() : undefined}
                    onValueChange={(value) => {
                      field.onChange(value ? parseInt(value) : 0);
                    }}
                    placeholder={isLoadingVenues ? "Loading venues..." : "Select venue"}
                    searchPlaceholder="Search venues..."
                    disabled={isLoadingVenues}
                    data-testid="promo-offer-form-venue-select"
                  />
                </FormControl>
                {selectedVenue && (
                  <div className="mt-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
                    <p className="font-medium">{selectedVenue.name}</p>
                    <p className="text-xs mt-1">{selectedVenue.formatted_address}</p>
                  </div>
                )}
                <FormDescription>
                  Select the venue this promo offer belongs to
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="brand"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Coca-Cola, Nike"
                  data-testid="promo-offer-form-brand"
                />
              </FormControl>
              <FormDescription>
                Sponsoring brand name (optional, max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="short_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Description</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Free drink with any purchase"
                  data-testid="promo-offer-form-short-description"
                />
              </FormControl>
              <FormDescription>
                Brief description shown in promo offer lists (max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="detailed_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Detailed Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter full promo offer details, terms and conditions..."
                  rows={4}
                  data-testid="promo-offer-form-detailed-description"
                />
              </FormControl>
              <FormDescription>
                Complete promo offer description with all details (optional, max 1000 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="media_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Promo Offer Image</FormLabel>
              <FormControl>
                <MediaUpload
                  value={field.value}
                  onChange={(url) => {
                    field.onChange(url);
                    // Trigger form validation for this field
                    form.trigger('media_url');
                  }}
                  onUpload={handleMediaUpload}
                  isUploading={isUploading}
                  onError={(error) => form.setError('media_url', { message: error })}
                />
              </FormControl>
              <FormDescription>
                Upload an image for this promo offer (optional, JPEG or PNG, max 5MB)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expires_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiry Date & Time</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  minDate={new Date()}
                  error={!!form.formState.errors.expires_at}
                />
              </FormControl>
              <FormDescription>
                When this promo offer expires (must be in the future)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="max_claim_per_person"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Claims Per Person</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    max={100}
                    placeholder="1"
                    data-testid="promo-offer-form-max-claim-per-person"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : 1)}
                  />
                </FormControl>
                <FormDescription>
                  Max claims per user (1-100)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_claim_total"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Total Claims</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    placeholder="100"
                    data-testid="promo-offer-form-max-claim-total"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : 1)}
                  />
                </FormControl>
                <FormDescription>
                  Total claims allowed
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active</FormLabel>
                <FormDescription>
                  Make this promo offer immediately available to users
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="promo-offer-form-is-active"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}